/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/camelcase */

import React from 'react';
import { withTheme } from 'styled-components';
import { BreakpointContainer } from '@deloitte-digital-au/dd-breakpoint-container';

import Image from 'core/sitecore/jss-proxy-components/JssImage';
import { IThemeObject } from 'pods/theme';
import Cta from 'core/components/Cta';
import CtaContainer from 'core/components/CtaContainer';
import Card from 'core/components/Card';
import {
	getBgProps,
	JSSprops as IBgProps,
} from 'pods/theme/components/BackgroundContainer';
import InlineNotification from 'core/components/InlineNotification';
import * as Typo from 'core/styles/Typography';
import { useAppContext, useTrackEvent, useModal } from 'core/hooks';

import {
	JssLinkType,
	getMessageByCode,
	getFormattedRichTextWithImage,
	JssTextType,
} from 'core/utils/sitecore-utils';
import { ERROR_CODES } from 'core/constants';
import TrackLinksWithin from 'core/analytics/TrackLinksWithin';

import { IFields } from './ContentBlock.sitecore';
import * as S from './ContentBlock.style';

interface IProps extends IFields {
	theme: IThemeObject;
	fields: IBgProps & IFields;
}

const ContentBlock = ({ fields, theme }: IProps) => {
	const {
		Image: image,
		description,
		heading: title,
		ctaButton,
		ctaRightButton,
	} = fields;
	const isSecondary = fields?.secondaryButtonStyle?.value ?? false;
	const imageAlignRight = fields?.imageAlignRight?.value ?? false;
	const hasImage = !!image?.value?.src;
	const hasButtons = !!ctaButton || !!ctaRightButton;
	const hasCtaDivider = (fields?.hasCtaDivider?.value ?? false) && hasButtons;
	const isDashboardModule = fields?.isDashboardModule?.value ?? false;
	const isImageCircle = fields?.isImageCircle?.value ?? false;
	const isContentCentered = fields?.isContentCentered?.value ?? false;
	const hasApiCall = fields?.hasApiCall?.value ?? false;

	const bgProps = getBgProps(fields);

	const {
		talSsoUrl,
		isTalSsoUrlLoading,
		handleTriggerTalSsoUrl,
		talSsoError,
		handleSetNotification: setNotification,
	} = useAppContext();

	/**
	 * A function to handle dynamic Typography for heading sitecore field
	 * This is for Dashboard VS other modules that might consume content block module.
	 */
	const displayTitle = () => {
		return (
			<>
				<Typo.HeadingSmall
					tag="h3"
					margin="0 0 16px"
					jss={title}
					data-target-id="content-block--title"
				/>
			</>
		);
	};
	// Analytics spreadsheet:
	// - Section: 'Modules'
	// - ID: 2.2.g
	const { trackEvent } = useTrackEvent({
		event_section: 'modules',
		event_type: 'content block',
		event_title: title?.value?.toLowerCase(),
	});

	// Start of hasApiCall
	const [Modal, toggleModal] = useModal();

	const [goToTalSsoCtaBtn, setGoToTalSsoCtaBtn] = React.useState<
		JssLinkType | undefined
	>();

	React.useEffect(() => {
		if (!!talSsoUrl) {
			setNotification(null);
			setGoToTalSsoCtaBtn({
				value: {
					text: fields?.talSSODsmModalCtaBtn?.value?.text ?? '',
					target: '_blank',
					href: talSsoUrl ?? 'javascript:void(0);',
				},
			});
		}
	}, [talSsoUrl]);

	React.useEffect(() => {
		if (!!goToTalSsoCtaBtn) {
			toggleModal(true);
		}
	}, [goToTalSsoCtaBtn]);

	React.useEffect(() => {
		// show error message when tal-sso failed
		if (!!talSsoError) {
			setNotification(null);

			const E72 = getMessageByCode(
				ERROR_CODES.TAL_SSO_NOT_AVAILABLE,
				fields.errorMessages,
				'errorMessage',
			);

			setNotification({
				content: E72,
				type: 'error',
			});

			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [talSsoError]);

	/**
	 * @note This is to handle any callbacks for API's
	 * For now only TAL is using it
	 */
	const hasApiCallHandler = () => {
		handleTriggerTalSsoUrl(true);
	};

	// End of hasApiCall

	return (
		<BreakpointContainer>
			{(bpName: string) => (
				<Card noBorder withShadow {...bgProps} noPadding={isDashboardModule}>
					{hasApiCall && (
						<Modal>
							<Typo.Body
								isRichText
								margin="0 0 25px"
								data-target-id={`content-block--modal-description`}
								jss={fields?.talSSODsmModalDescription}
							/>
							<CtaContainer stackAt={null} margin="0 0 -25px">
								<Cta
									ctaStyle="primary"
									data-target-id={`content-block--modal-button`}
									onClick={toggleModal}
									jssLink={goToTalSsoCtaBtn}
								>
									{fields?.talSSODsmModalCtaBtn?.value?.text}
								</Cta>
							</CtaContainer>
						</Modal>
					)}
					<S.ContentBlock
						{...{
							imageAlignRight,
							bpName,
							hasImage,
							isImageCircle,
							isContentCentered,
							isDashboardModule,
						}}
						data-target-id="content-block"
					>
						{!!fields?.notificationContent?.value && (
							<S.NotificationBlock
								alignTop={
									fields?.notificationContentPosition?.value !== 'bottom'
								}
							>
								<InlineNotification
									isRichText
									jss={fields?.notificationContent}
									type={fields?.notificationContentType?.value ?? 'info'}
									data-target-id="content-block--notification"
								/>
							</S.NotificationBlock>
						)}
						{hasImage && (
							<S.ImageBlock {...{ isDashboardModule }}>
								<Image field={image} data-target-id="content-block--image" />
							</S.ImageBlock>
						)}
						<S.TextBlock>
							<S.Text isDashboardModule={isDashboardModule}>
								{displayTitle()}
								{description && (
									<TrackLinksWithin
										targetLinks={[
											[
												'cb-rte-link-external',
												// Analytics spreadsheet:
												// - Section: 'Modules'
												({ text, link }) =>
													trackEvent({
														event_label: text?.toLowerCase(),
														event_trigger: 'click link',
														event_url: link,
													}),
											],
										]}
									>
										<Typo.RichText
											jss={
												getFormattedRichTextWithImage(
													description,
												) as JssTextType
											}
											data-target-id="content-block--description"
										/>
									</TrackLinksWithin>
								)}
								{hasCtaDivider && <S.Hr />}

								{hasButtons && (
									<S.CtaWrapper isDashboardModule={isDashboardModule}>
										{!!ctaButton?.value?.text && (
											<Cta
												ctaType={hasApiCall ? 'button' : undefined}
												ctaStyle={isSecondary ? 'secondary' : 'primary'}
												jssLink={hasApiCall ? undefined : ctaButton}
												data-target-id="content-block--cta-primary"
												isInverted={theme.util.shouldCtaInvert(bgProps)}
												onClick={() => {
													hasApiCallHandler();
													// Analytics spreadsheet:
													// - Section: 'Modules'
													// - ID: 2.2.g
													trackEvent({
														event_label: ctaButton.value?.text.toLowerCase(),
														event_trigger: 'click button',
														event_url: ctaButton.value?.href,
													});
												}}
												/**
												 * Note: If there will be other scenarios like this for the ContentBlock
												 * then we should refactor isLoadingState to only cater for a particular API
												 * and not directly on TAL if there is an API call
												 *
												 * e.g. {hasApiCall ? (api0 || api1) : false}
												 */
												isLoading={hasApiCall ? isTalSsoUrlLoading : false}
												isDisabled={hasApiCall ? isTalSsoUrlLoading : false}
											>
												{hasApiCall ? ctaButton?.value?.text : undefined}
											</Cta>
										)}
										{!!ctaRightButton?.value?.text && (
											<S.SecondaryCtaWrapper>
												<Cta
													ctaStyle="plain"
													jssLink={ctaRightButton}
													data-target-id="content-block--cta-secondary"
													isInverted={theme.util.shouldCtaInvert(bgProps)}
													onClick={() => {
														// Analytics spreadsheet:
														// - Section: 'Modules'
														// - ID: 2.2.g
														trackEvent({
															event_label: ctaRightButton.value?.text.toLowerCase(),
															event_trigger: 'click button',
															event_url: ctaRightButton.value?.href,
														});
													}}
												/>
											</S.SecondaryCtaWrapper>
										)}
									</S.CtaWrapper>
								)}
							</S.Text>
						</S.TextBlock>
					</S.ContentBlock>
				</Card>
			)}
		</BreakpointContainer>
	);
};

export default withTheme(ContentBlock);
